import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import { formatCurrencyUSD } from "../../utils/format";
import "../../styles/ProductionPulseTable.css";
import { saveGoalToSheet } from "../../utils/saveGoalToSheet";
import PP_NegotiatorDetailModal from "../../components/PP_NegotiatorDetailModal";
import { fetchGoalsFromSheet as fetchGoalsFromSheetUtil } from "../../utils/fetchGoalsFromSheet";
import { Bar } from "react-chartjs-2";
import { FaEye, FaFileExport, FaSpinner, FaTimes } from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function PP_partial_Summary() {
  // Data and UI state
  const [data, setData] = useState([]);
  const [selectedNegotiator, setSelectedNegotiator] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [applyLoading, setApplyLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [goals, setGoals] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalNegotiator, setModalNegotiator] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(300);
  const [viewMode, setViewMode] = useState('detailed');

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Initial current month range
  const initialRange = {
    start: new Date(currentYear, currentMonth, 1).toISOString().split('T')[0],
    end: new Date(currentYear, currentMonth + 1, 0).toISOString().split('T')[0],
  };

  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [dateRangeType, setDateRangeType] = useState('current_month');
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [appliedStartDate, setAppliedStartDate] = useState(initialRange.start);
  const [appliedEndDate, setAppliedEndDate] = useState(initialRange.end);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);

  // State for team-wide goal
  const [teamGoal, setTeamGoal] = useState(0);
  const [isEditingTeamGoal, setIsEditingTeamGoal] = useState(false);
  const [teamGoalInput, setTeamGoalInput] = useState("");

  // Goal column visibility state
  const [hidePastGoalColumns, setHidePastGoalColumns] = useState(false);

  // Fetch team goal from Google Sheets
  const fetchTeamGoal = useCallback(async () => {
    try {
      // Use a special identifier for team goal
      const goalData = await fetchGoalsFromSheetUtil(appliedStartDate);
      
      // Look for a special "Team Goal" entry
      if (Array.isArray(goalData)) {
        const teamGoalEntry = goalData.find(item => 
          (item.fullName || item.FullName) === 'Team Goal' || 
          (item.fullName || item.FullName) === 'TEAM_GOAL'
        );
        if (teamGoalEntry) {
          const goalValue = parseFloat(teamGoalEntry.goal || teamGoalEntry.Goal) || 0;
          setTeamGoal(goalValue);
        }
      } else if (goalData && typeof goalData === 'object') {
        const goalValue = goalData['Team Goal'] || goalData['TEAM_GOAL'] || 0;
        setTeamGoal(parseFloat(goalValue) || 0);
      }
    } catch (err) {
      console.error("Error fetching team goal:", err);
    }
  }, [appliedStartDate]);

  // Save team goal to Google Sheets
  const saveTeamGoal = async (newGoal) => {
    try {
      await saveGoalToSheet('Team Goal', newGoal);
      setTeamGoal(newGoal);
      Swal.fire({
        icon: "success",
        title: "Team Goal Saved!",
        text: `Team goal has been updated to ${newGoal.toLocaleString()}.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Save team goal error:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to save",
        text: "Could not update the team goal. Please try again.",
      });
    }
  };

  // Handle team goal edit
  const handleTeamGoalEdit = () => {
    setTeamGoalInput(teamGoal.toString());
    setIsEditingTeamGoal(true);
  };

  const handleTeamGoalSave = () => {
    const newGoal = parseFloat(teamGoalInput) || 0;
    saveTeamGoal(newGoal);
    setIsEditingTeamGoal(false);
  };

  const handleTeamGoalCancel = () => {
    setIsEditingTeamGoal(false);
    setTeamGoalInput("");
  };

  // Fetch team goal on mount and when dates change
  useEffect(() => {
    if (!initialLoading) {
      fetchTeamGoal();
    }
  }, [fetchTeamGoal, initialLoading]);

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState("");
  const [negotiatorFilter, setNegotiatorFilter] = useState("");

  // Drilldown state - inline below table
  const [drilldownNegotiator, setDrilldownNegotiator] = useState("");
  const [drilldownData, setDrilldownData] = useState([]);
  const [drilldownTotalFee, setDrilldownTotalFee] = useState(0);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  // State to store production counts for each negotiator
  const [negotiatorProductionCounts, setNegotiatorProductionCounts] = useState({});

  // Function to fetch and calculate production counts for all negotiators
  const fetchAllProductionCounts = useCallback(async () => {
    try {
      const counts = {};
      
      // Get unique negotiators from aggregated data
      const negotiators = [...new Set(data.map(item => item.AssignedNegotiator))];
      
      console.log(`🔍 Fetching production counts for ${negotiators.length} negotiators`);
      console.log(`📅 Date Range: ${appliedStartDate} to ${appliedEndDate}`);
      
      // Fetch settlements for each negotiator
      const countPromises = negotiators.map(async (negotiatorName) => {
        try {
          const params = new URLSearchParams({
            AssignedNegotiator: negotiatorName,
          });

          if (appliedStartDate) {
            params.append('DateStart', new Date(appliedStartDate).toISOString().split('T')[0]);
          }
          if (appliedEndDate) {
            params.append('DateEnd', new Date(appliedEndDate).toISOString().split('T')[0]);
          }

          const url = `https://eo2pmt1i9qx0gwt.m.pipedream.net?${params.toString()}`;
          const res = await fetch(url);
          
          if (!res.ok) return;

          const result = await res.json();
          let allSettlements = [];

          if (result && typeof result === 'object') {
            if (result[negotiatorName]) {
              allSettlements = result[negotiatorName];
            } else {
              const negotiatorKey = Object.keys(result).find(
                key => key.toLowerCase() === negotiatorName.toLowerCase()
              );
              if (negotiatorKey) {
                allSettlements = result[negotiatorKey];
              }
            }
          } 
          
          if (Array.isArray(result)) {
            allSettlements = result;
          } 
          
          if (result.data && Array.isArray(result.data)) {
            allSettlements = result.data;
          }

          // Count production statuses
          const productionStatuses = [
            "missing letter",
            "missing letter (pays submitted)",
            "completed",
            "ready for processing",
            "ready for full processing",
            "ready for partial processing",
          ];

          const productionCount = allSettlements.filter(s => {
            const status = (s.OfferStatus || s.Status || "").trim().toLowerCase();
            return productionStatuses.some(prodStatus => 
              status === prodStatus.toLowerCase()
            );
          }).length;

          console.log(`✅ ${negotiatorName}: ${allSettlements.length} total settlements, ${productionCount} production settlements`);
          counts[negotiatorName] = productionCount;
        } catch (err) {
          console.error(`Error fetching for ${negotiatorName}:`, err);
          counts[negotiatorName] = 0;
        }
      });

      await Promise.all(countPromises);
      setNegotiatorProductionCounts(counts);
    } catch (err) {
      console.error("Error fetching production counts:", err);
    }
  }, [data, appliedStartDate, appliedEndDate]);

  // Fetch production counts when data or dates change
  useEffect(() => {
    if (data.length > 0 && !initialLoading) {
      fetchAllProductionCounts();
    }
  }, [data, appliedStartDate, appliedEndDate, initialLoading, fetchAllProductionCounts]);

  // Refs for charts and applied dates
  const appliedDatesRef = useRef({ start: "", end: "" });
  const goalChartRef = useRef(null);
  const compChartRef = useRef(null);
  const drilldownRef = useRef(null);

  // Helper function to get date ranges
  const getDateRange = useCallback((type) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDate = now.getDate();

    switch (type) {
      case 'current_month': {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        return {
          start: firstDay.toISOString().split('T')[0],
          end: lastDay.toISOString().split('T')[0],
        };
      }
      case 'today': {
        const today = new Date(currentYear, currentMonth, currentDate);
        const formattedToday = today.toISOString().split('T')[0];
        return { start: formattedToday, end: formattedToday };
      }
      case 'this_week': {
        const dayOfWeek = now.getDay();
        const startOfWeek = new Date(currentYear, currentMonth, currentDate - dayOfWeek);
        const endOfWeek = new Date(currentYear, currentMonth, currentDate + (6 - dayOfWeek));
        return {
          start: startOfWeek.toISOString().split('T')[0],
          end: endOfWeek.toISOString().split('T')[0],
        };
      }
      case 'custom': {
        return {
          start: startDate || initialRange.start,
          end: endDate || initialRange.end,
        };
      }
      default:
        return {
          start: initialRange.start,
          end: initialRange.end,
        };
    }
  }, [startDate, endDate, initialRange.start, initialRange.end]);

  const getStatusValue = (item) =>
    item.Status || item.OfferStatus || item.Offer_Status || item.OfferState || item.StatusName || "";

  // Check date for goal column visibility
  useEffect(() => {
    const cutoffDate = new Date('2025-09-01');
    const appliedStart = appliedStartDate ? new Date(appliedStartDate) : null;
    
    setHidePastGoalColumns(appliedStart && appliedStart <= cutoffDate);
  }, [appliedStartDate]);

  // Update ref whenever applied dates change
  useEffect(() => {
    appliedDatesRef.current = { start: appliedStartDate, end: appliedEndDate };
  }, [appliedStartDate, appliedEndDate]);

  useEffect(() => {
    setDateRange(getDateRange(dateRangeType));
  }, [dateRangeType, getDateRange]);

  useEffect(() => {
    const initialRange = getDateRange("select");
    setStartDate(initialRange.start);
    setEndDate(initialRange.end);
  }, [getDateRange]);

  useEffect(() => {
    const { start, end } = getDateRange(dateRangeType);
    setStartDate(start);
    setEndDate(end);
    setShowCustomDateInputs(dateRangeType === 'custom');
  }, [dateRangeType, getDateRange]);

  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };

    setStartDate(formatDate(firstDay));
    setEndDate(formatDate(lastDay));
  }, []);

  // Helper functions
  const formatMonthYear = (dateString) => {
    if (!dateString) return 'Current Month';
    const date = new Date(dateString);
    date.setDate(1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const formatDateLabel = (startDate, endDate) => {
    if (!startDate && !endDate) return 'Current Month';
    
    const parseDate = (dateString) => {
      if (!dateString) return null;
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day, 12, 0, 0);
      return date;
    };

    const start = parseDate(startDate);
    const end = parseDate(endDate);

    if (start && end && 
        start.getMonth() === end.getMonth() && 
        start.getFullYear() === end.getFullYear()) {
      return new Intl.DateTimeFormat('en-US', { 
        month: 'long', 
        year: 'numeric' 
      }).format(start);
    }

    const formatOptions = { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    };

    const formatDate = (date) => 
      date ? date.toLocaleDateString('en-US', formatOptions) : '';

    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const getDateValue = (item) => {
    const dateFields = [
      'CreatedDate', 
      'Date', 
      'SettlementDate', 
      'OfferDate', 
      'CreatedAt'
    ];

    for (let field of dateFields) {
      if (item[field]) {
        const date = new Date(item[field]);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    return null;
  };

  // Fetch production data
  const fetchProductionData = useCallback(async (dateStart = null, dateEnd = null) => {
    try {
      let url = "https://eosn9feqj1gxhvh.m.pipedream.net";
      let options = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      };

      if (dateStart || dateEnd) {
        options.method = 'POST';
        options.body = JSON.stringify({
          DateStart: dateStart ? new Date(dateStart).toISOString().split('T')[0] : null,
          DateEnd: dateEnd ? new Date(dateEnd).toISOString().split('T')[0] : null
        });
      }

      const res = await fetch(url, options);
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Production fetch failed: ${res.status} - ${errorText}`);
      }

      const json = await res.json();
      
      const productionData = json.ProductionPulse || json;
      
      setData(productionData);
      setErrorMsg("");
      return productionData;
    } catch (err) {
      console.error("Detailed Fetch Error:", err);
      setErrorMsg(`⚠️ Could not load data: ${err.message}`);
      throw err;
    }
  }, []);

  // Fetch goals
  const fetchGoalsFromSheet = useCallback(async () => {
    try {
      if (!appliedStartDate || !appliedEndDate) {
        console.warn("⚠️ No date range selected");
        setGoals({});
        return {};
      }

      console.log(`🎯 Fetching goals for Date Range: ${appliedStartDate} → ${appliedEndDate}`);

      let rawGoals = await fetchGoalsFromSheetUtil(appliedStartDate);

      console.log("✅ Raw Goals from Sheet:", rawGoals);

      let monthGoals = {};

      if (Array.isArray(rawGoals)) {
        const sorted = rawGoals.sort((a, b) => {
          const timeA = new Date(a.timestamp || 0).getTime();
          const timeB = new Date(b.timestamp || 0).getTime();
          return timeB - timeA;
        });

        const seen = new Set();
        sorted.forEach(item => {
          const name = item.fullName || item.FullName;
          if (name && !seen.has(name)) {
            seen.add(name);
            const goalValue = parseFloat(item.goal || item.Goal) || 0;
            monthGoals[name] = goalValue;
          }
        });
      } else if (rawGoals && typeof rawGoals === 'object') {
        for (const [negotiator, goal] of Object.entries(rawGoals)) {
          const goalValue = typeof goal === 'number' ? goal : parseFloat(goal) || 0;
          if (goalValue >= 0) {
            monthGoals[negotiator] = goalValue;
          } else {
            console.warn(`⚠️ Ignoring invalid goal for ${negotiator}: ${goal}`);
          }
        }
      }

      console.log("✅ Cleaned Goals (Latest Only):", monthGoals);

      setGoals(monthGoals);
      return monthGoals;

    } catch (err) {
      console.error("❌ Fetch goals error:", err);
      setErrorMsg("⚠️ Could not load goals from sheet. Please retry.");
      setGoals({});
      return {};
    }
  }, [appliedStartDate, appliedEndDate]);

  // Initial load effect
  useEffect(() => {
    let mounted = true;
    let countdownInterval;
    let refreshInterval;

    async function initialLoad() {
      if (!mounted) return;
      
      setInitialLoading(true);
      setErrorMsg("");

      try {
        await Promise.all([
          fetchProductionData(null, null),
          fetchGoalsFromSheet()
        ]);
      } catch (err) {
        console.error("Initial load error:", err);
        if (mounted) setErrorMsg("⚠️ Could not load data. Please retry.");
      } finally {
        if (mounted) setInitialLoading(false);
      }
    }

    initialLoad();

    countdownInterval = setInterval(() => {
      setAutoRefreshCountdown(prevCount => 
        prevCount <= 1 ? 300 : prevCount - 1
      );
    }, 1000);

    refreshInterval = setInterval(async () => {
      if (!mounted) return;
      
      try {
        const { start, end } = appliedDatesRef.current;
        await Promise.all([
          fetchProductionData(start, end),
          fetchGoalsFromSheet()
        ]);
        setAutoRefreshCountdown(300);
      } catch (err) {
        console.error("Auto-refresh error:", err);
        if (mounted) setErrorMsg("⚠️ Could not auto-refresh data.");
      }
    }, 300000);

    return () => {
      mounted = false;
      if (countdownInterval) clearInterval(countdownInterval);
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [fetchProductionData, fetchGoalsFromSheet]);

  // Goal handling methods
  const reloadGoals = useCallback(async () => {
    await fetchGoalsFromSheet();
  }, [fetchGoalsFromSheet]);

  const handleGoalChange = async (negotiatorName, newGoal) => {
    setGoals((prevGoals) => ({
      ...prevGoals,
      [negotiatorName]: newGoal,
    }));

    try {
      await saveGoalToSheet(negotiatorName, newGoal);
      Swal.fire({
        icon: "success",
        title: "Goal Saved!",
        text: `${negotiatorName}'s goal has been updated.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Save goal error:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to save",
        text: "Could not update the goal. Please try again.",
      });
      await reloadGoals();
    }
  };

  const handleGoalSaved = async () => {
    await reloadGoals();
    const { start, end } = appliedDatesRef.current;
    await fetchProductionData(start, end);
    closeModal();
  };

  // Filtered data logic
  const filteredData = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    
    const parseDate = (dateString) => {
      if (!dateString) return null;
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    };

    const sDate = appliedStartDate 
      ? parseDate(appliedStartDate).toISOString().split('T')[0]
      : null;
    
    const eDate = appliedEndDate 
      ? parseDate(appliedEndDate).toISOString().split('T')[0]
      : null;
      
    return data.filter((item) => {
      const name = (item.AssignedNegotiator || "").toString();
      const matchesName = name.toLowerCase().includes(text);

      const matchesDropdown = selectedNegotiator ? item.AssignedNegotiator === selectedNegotiator : true;
      const matchesCross = negotiatorFilter ? item.AssignedNegotiator === negotiatorFilter : true;
      const statusVal = getStatusValue(item);
      const matchesStatus = selectedStatus ? String(statusVal) === String(selectedStatus) : true;

      const itemDate = item.CreatedDate 
        ? item.CreatedDate.split('T')[0]
        : null;

      let matchesDate = true;

      if ((sDate || eDate) && itemDate) {
        matchesDate = (!sDate || itemDate >= sDate) && 
                      (!eDate || itemDate <= eDate);
      } else {
        if ((sDate || eDate) && !itemDate) {
          matchesDate = false;
        }
      }

      return matchesName && matchesDropdown && matchesCross && matchesStatus && matchesDate;
    });
  }, [data, searchText, selectedNegotiator, negotiatorFilter, selectedStatus, appliedStartDate, appliedEndDate]);

  // Aggregate by Negotiator
  const aggregateByNegotiator = useCallback((dataToAggregate) => {
    const aggregatedData = {};

    dataToAggregate.forEach(item => {
      const negotiator = item.AssignedNegotiator;
      
      if (!aggregatedData[negotiator]) {
        aggregatedData[negotiator] = {
          negotiator: negotiator,
          entries: [],
          totalProductionToDate: 0,
          totalProductionUnderway: 0,
          totalSIFsUnderway: 0,
          biggestFeePending: 0, // Changed from total to biggest
          totalIndividualFee: 0,
          totalProductionPotential: 0,
          dates: new Set(),
          goal: goals[negotiator] || 0,
          neededToGoal: 0,
          percentFromGoal: "0.00"
        };
      }
      
      const negotiatorData = aggregatedData[negotiator];
      negotiatorData.entries.push(item);
      
      negotiatorData.totalProductionToDate += item.ProductionToDateOTB || 0;
      negotiatorData.totalProductionUnderway += item.TotalProductionUnderway || 0;
      negotiatorData.totalSIFsUnderway += item.TotalSIFsUnderway || 0;
      
      // Get the highest BiggestFeePending instead of summing
      const currentFee = item.BiggestFeePending || 0;
      if (currentFee > negotiatorData.biggestFeePending) {
        negotiatorData.biggestFeePending = currentFee;
      }
      
      negotiatorData.totalIndividualFee += item.IndividualFee || 0;
      negotiatorData.totalProductionPotential += item.ProductionPotential || 0;
      
      negotiatorData.dates.add(item.CreatedDate ? item.CreatedDate.split('T')[0] : 'Unknown');
    });

    return Object.values(aggregatedData).map(negotiatorData => {
      if (!hidePastGoalColumns) {
        negotiatorData.neededToGoal = negotiatorData.goal - negotiatorData.totalProductionToDate;
        negotiatorData.percentFromGoal = negotiatorData.goal > 0 
          ? ((negotiatorData.totalProductionToDate / negotiatorData.goal) * 100).toFixed(2)
          : "0.00";
      } else {
        negotiatorData.goal = 0;
        negotiatorData.neededToGoal = 0;
        negotiatorData.percentFromGoal = "0.00";
      }

      return {
        ...negotiatorData,
        dates: Array.from(negotiatorData.dates).join(', '),
        entriesCount: negotiatorData.entries.length,
        lastEntry: negotiatorData.entries[negotiatorData.entries.length - 1]
      };
    });
  }, [goals, hidePastGoalColumns]);

  const aggregatedData = useMemo(() => {
    return aggregateByNegotiator(filteredData);
  }, [filteredData, aggregateByNegotiator]);

  // Chart and calculation memoized values
  const chartLabels = useMemo(() => aggregatedData.map((item) => item.negotiator), [aggregatedData]);
  const currentProduction = useMemo(() => aggregatedData.map((item) => item.totalProductionToDate), [aggregatedData]);
  const workingPipeline = useMemo(() => aggregatedData.map((item) => item.totalProductionUnderway), [aggregatedData]);

  const goalsList = useMemo(() => 
    hidePastGoalColumns 
      ? aggregatedData.map(() => 0) 
      : aggregatedData.map((item) => item.goal), 
  [aggregatedData, hidePastGoalColumns]);

  const goalAchievement = useMemo(() => 
    hidePastGoalColumns 
      ? aggregatedData.map(() => 0) 
      : aggregatedData.map((item) =>
      item.goal > 0 ? Math.round((item.totalProductionToDate / item.goal) * 100) : 0
    ), 
  [aggregatedData, hidePastGoalColumns]);

  // const totals = useMemo(() => {
  //   const negotiatorDetails = aggregatedData
  //     .map(negotiatorData => ({
  //       name: negotiatorData.negotiator,
  //       goal: negotiatorData.goal,
  //       totalProductionToDate: negotiatorData.totalProductionToDate,
  //       pct: negotiatorData.goal > 0
  //         ? Math.round((negotiatorData.totalProductionToDate / negotiatorData.goal) * 100)
  //         : 0
  //     }))
  //     .sort((a, b) => b.pct - a.pct);

  //   const totalProduction = aggregatedData.reduce((acc, item) => acc + item.totalProductionToDate, 0);
    
  //   const totalGoal = !hidePastGoalColumns
  //     ? aggregatedData.reduce((acc, item) => {
  //         const goal = typeof item.goal === 'number' ? item.goal : 0;
  //         return acc + goal;
  //       }, 0)
  //     : 0;

  //   const avgPct = !hidePastGoalColumns && negotiatorDetails.length > 0
  //     ? Math.round(negotiatorDetails.reduce((sum, n) => sum + n.pct, 0) / negotiatorDetails.length)
  //     : 0;

  //   const topByProduction = aggregatedData.length > 0
  //     ? aggregatedData.reduce((topNeg, current) => 
  //         current.totalProductionToDate > topNeg.totalProductionToDate ? current : topNeg
  //       )
  //     : null;

  //   console.log('🔍 TOTALS DEBUG:');
  //   console.log('Aggregated Data Count:', aggregatedData.length);
  //   console.log('Total Goal Breakdown:', aggregatedData.map(n => ({ name: n.negotiator, goal: n.goal })));
  //   console.log('Total Goal Sum:', totalGoal);
  //   console.log('Total Production:', totalProduction);

  //   return { 
  //     totalProduction, 
  //     totalGoal, 
  //     avgPct, 
  //     top: topByProduction ? { 
  //       name: topByProduction.negotiator, 
  //       production: topByProduction.totalProductionToDate 
  //     } : null
  //   };
  // }, [aggregatedData, hidePastGoalColumns]);

  const totals = useMemo(() => {
  const negotiatorDetails = aggregatedData
    .map(negotiatorData => ({
      name: negotiatorData.negotiator,
      goal: negotiatorData.goal,
      totalProductionToDate: negotiatorData.totalProductionToDate,
      pct:
        negotiatorData.goal > 0
          ? Math.round(
              (negotiatorData.totalProductionToDate / negotiatorData.goal) * 100
            )
          : 0
    }))
    .sort((a, b) => b.pct - a.pct);

  const totalProduction = aggregatedData.reduce(
    (acc, item) => acc + item.totalProductionToDate,
    0
  );

  const totalGoal = !hidePastGoalColumns
    ? aggregatedData.reduce((acc, item) => {
        const goal = typeof item.goal === "number" ? item.goal : 0;
        return acc + goal;
      }, 0)
    : 0;

  const avgPct =
    !hidePastGoalColumns && negotiatorDetails.length > 0
      ? Math.round(
          negotiatorDetails.reduce((sum, n) => sum + n.pct, 0) /
            negotiatorDetails.length
        )
      : 0;

  // ✅ FIXED — take top from negotiatorDetails (sorted by pct)
  const top = negotiatorDetails.length > 0 ? negotiatorDetails[0] : null;

  return {
    totalProduction,
    totalGoal,
    avgPct,
    top: top
      ? {
          name: top.name,
          pct: top.pct // <-- Now UI gets the percentage
        }
      : null
  };
}, [aggregatedData, hidePastGoalColumns]);


  // Modal and drilldown handlers
  const openModal = (negotiatorName) => {
    const aggregatedNegotiatorData = aggregatedData.find(data => data.negotiator === negotiatorName);

    if (aggregatedNegotiatorData) {
      const modalData = {
        AssignedNegotiator: aggregatedNegotiatorData.negotiator,
        goal: aggregatedNegotiatorData.goal,
        ProductionToDateOTB: aggregatedNegotiatorData.totalProductionToDate,
        TotalProductionUnderway: aggregatedNegotiatorData.totalProductionUnderway,
        TotalSIFsUnderway: aggregatedNegotiatorData.totalSIFsUnderway,
        BiggestFeePending: aggregatedNegotiatorData.biggestFeePending,
        IndividualFee: aggregatedNegotiatorData.totalIndividualFee,
        IndividualSIFCount: aggregatedNegotiatorData.totalSIFsUnderway,
        percentFromGoal: aggregatedNegotiatorData.percentFromGoal
      };

      setModalNegotiator(modalData);
      setModalOpen(true);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalNegotiator(null);
  };

  const openDrilldown = async (negotiatorName) => {
    try {
      setDrilldownNegotiator(negotiatorName);
      setDrilldownData([]);
      setDrilldownTotalFee(0);
      setDrilldownLoading(true);

      setTimeout(() => {
        drilldownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);

      const params = new URLSearchParams({
        AssignedNegotiator: negotiatorName,
      });

      if (appliedStartDate) {
        params.append('DateStart', new Date(appliedStartDate).toISOString().split('T')[0]);
      }
      if (appliedEndDate) {
        params.append('DateEnd', new Date(appliedEndDate).toISOString().split('T')[0]);
      }

      const url = `https://eo2pmt1i9qx0gwt.m.pipedream.net?${params.toString()}`;

      const res = await fetch(url);
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Drilldown API Error: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      
      let allSettlements = [];

      if (result && typeof result === 'object') {
        if (result[negotiatorName]) {
          allSettlements = result[negotiatorName];
        } else {
          const negotiatorKey = Object.keys(result).find(
            key => key.toLowerCase() === negotiatorName.toLowerCase()
          );
          if (negotiatorKey) {
            allSettlements = result[negotiatorKey];
          }
        }
      } 
      
      if (Array.isArray(result)) {
        allSettlements = result;
      } 
      
      if (result.data && Array.isArray(result.data)) {
        allSettlements = result.data;
      }

      const totalFee = allSettlements.reduce((sum, s) => {
        const fee = parseFloat(s.SettlementFee || s.Fee || 0);
        return sum + (isNaN(fee) ? 0 : fee);
      }, 0);

      setDrilldownData(allSettlements);
      setDrilldownTotalFee(totalFee);

      if (allSettlements.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No Settlements Found",
          text: `No settlement details found for ${negotiatorName} in the selected date range.`,
          timer: 2000,
          showConfirmButton: false
        });
      }

    } catch (err) {
      console.error("Error fetching drilldown data:", err);
      
      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: `Failed to load settlement details: ${err.message}. Please check your connection and try again.`,
        confirmButtonText: "OK"
      });
      
      setDrilldownData([]);
      setDrilldownTotalFee(0);
    } finally {
      setDrilldownLoading(false);
    }
  };

  const closeDrilldown = () => {
    setDrilldownNegotiator("");
    setDrilldownData([]);
  };

  // Date filter and reset handlers
  const handleDateFilter = useCallback(
    async (start = startDate, end = endDate, rangeType = dateRangeType) => {
      try {
        setApplyLoading(true);
        setErrorMsg('');
        setAutoRefreshCountdown(300);

        const formatDate = (date) => new Date(date).toISOString().split('T')[0];

        const formattedStart = formatDate(start);
        const formattedEnd = formatDate(end);

        setStartDate(formattedStart);
        setEndDate(formattedEnd);
        setAppliedStartDate(formattedStart);
        setAppliedEndDate(formattedEnd);

        appliedDatesRef.current = { start: formattedStart, end: formattedEnd };

        await Promise.all([
          fetchProductionData(formattedStart, formattedEnd),
          fetchGoalsFromSheet(),
        ]);

        console.log('Date Range Type:', rangeType);
        console.log('Fetched Start Date:', formattedStart);
        console.log('Fetched End Date:', formattedEnd);
      } catch (error) {
        console.error('Apply filter error:', error);
        setErrorMsg(`Failed to apply filter: ${error.message}`);
      } finally {
        setApplyLoading(false);
      }
    },
    [startDate, endDate, dateRangeType, fetchProductionData, fetchGoalsFromSheet]
  );

  const handleReset = useCallback(async () => {
    try {
      setResetLoading(true);
      
      setDateRangeType('current_month');
      
      const { start, end } = getDateRange('current_month');

      setSelectedStatus("");
      setStartDate(start);
      setEndDate(end);
      setNegotiatorFilter("");
      setSelectedNegotiator("");
      setSearchText("");
      setAutoRefreshCountdown(300);
      
      setAppliedStartDate(start);
      setAppliedEndDate(end);
      
      await Promise.all([
        fetchProductionData(start, end),
        fetchGoalsFromSheet()
      ]);
    } catch (error) {
      console.error('Reset error:', error);
      setErrorMsg(`Failed to reset: ${error.message}`);
    } finally {
      setResetLoading(false);
    }
  }, [getDateRange, fetchProductionData, fetchGoalsFromSheet]);

  // Export CSV
  const exportCSV = () => {
    const header = [
      "Name",
      "Goal",
      "Entries",
      "Total Production To Date",
      "Total Production Underway", 
      "Total SIFs Underway",
      "Total Biggest Fee Pending",
      "Total Individual Fee",
      "Total Production Potential",
      "Needed to Goal",
      "% From Goal"
    ];
    const rowsCsv = [header.join(",")];

    aggregatedData.forEach((negotiatorData) => {
      const row = [
        `"${negotiatorData.negotiator}"`,
        negotiatorData.goal,
        negotiatorData.entriesCount,
        negotiatorData.totalProductionToDate,
        negotiatorData.totalProductionUnderway,
        negotiatorData.totalSIFsUnderway,
        negotiatorData.totalBiggestFeePending,
        negotiatorData.totalIndividualFee,
        negotiatorData.totalProductionPotential,
        negotiatorData.neededToGoal,
        negotiatorData.percentFromGoal
      ].join(",");

      rowsCsv.push(row);
    });

    const blob = new Blob([rowsCsv.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const getMonthName = (dateString) => {
      if (!dateString) return 'Current_Month';
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day, 12, 0, 0);
      return date.toLocaleString('default', { month: 'long' });
    };

    const fileName = `negotiator_performance_${getMonthName(appliedStartDate)}_${new Date(appliedStartDate).getFullYear()}.csv`;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Bar click handler
  const handleBarClick = (event, elements, chartLabelsSource = chartLabels) => {
    try {
      if (elements && elements.length > 0) {
        const idx = elements[0].index;
        const name = chartLabelsSource[idx];
        if (name) {
          openDrilldown(name);
        }
      }
    } catch (err) {
      console.error("Bar click error:", err);
    }
  };

  // Skeleton loading state
  if (initialLoading) {
    return (
      <div className="pulse-skeleton-container">
        <h2 className="skeleton-title">Production Pulse</h2>
        <div className="skeleton-alert shimmer"></div>
        <div className="skeleton-row">
          <div className="skeleton-card shimmer"></div>
          <div className="skeleton-card shimmer"></div>
          <div className="skeleton-card shimmer"></div>
          <div className="skeleton-card shimmer"></div>
        </div>
        <div className="skeleton-row">
          <div className="skeleton-chart shimmer"></div>
          <div className="skeleton-chart shimmer"></div>
        </div>
        <div className="skeleton-table">
          <div className="skeleton-table-header shimmer"></div>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton-table-row shimmer"></div>
          ))}
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="summary-container">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .drilldown-section {
          margin-top: 30px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 2px solid #e0e0e0;
        }
        
        .drilldown-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #dee2e6;
        }
        
        .drilldown-table-container {
          background: white;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .drilldown-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .drilldown-table th {
          background: #2c3e50;
          color: white;
          padding: 12px;
          text-align: left;
          font-weight: 600;
        }
        
        .drilldown-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #e0e0e0;
        }
        
        .drilldown-table tbody tr:hover {
          background: #f8f9fa;
        }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.85em;
          font-weight: 500;
        }
        
        .status-completed {
          background: #d1fae5;
          color: #065f46;
        }
        
        .status-working {
          background: #fef3c7;
          color: #92400e;
        }
        
        .status-default {
          background: #e5e7eb;
          color: #374151;
        }
      `}</style>

      <h2 style={{ textAlign: "left" }}>
        Negotiator Performance for {formatDateLabel(appliedStartDate, appliedEndDate)}
      </h2>
      
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <div
          style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}
        >
          <label style={{ fontWeight: 600 }}>Date Range:</label>
          <select
            value={dateRangeType}
            onChange={(e) => {
              const newRangeType = e.target.value;
              setDateRangeType(newRangeType);

              if (newRangeType !== 'custom') {
                const { start, end } = getDateRange(newRangeType);
                handleDateFilter(start, end, newRangeType);
              }
            }}
            style={{ padding: '6px 10px', borderRadius: '6px' }}
          >
            <option value="current_month">Current Month</option>
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="custom">Custom Range</option>
          </select>

          {showCustomDateInputs && (
            <>
              <label style={{ fontWeight: 600 }}>Start:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={applyLoading || resetLoading}
                style={{ opacity: (applyLoading || resetLoading) ? 0.6 : 1 }}
              />

              <label style={{ fontWeight: 600 }}>End:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={applyLoading || resetLoading}
                style={{ opacity: (applyLoading || resetLoading) ? 0.6 : 1 }}
              />
            </>
          )}

          <button
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              background: applyLoading ? "#95a5a6" : "#2c3e50",
              color: "white",
              border: "none",
              cursor: applyLoading ? "not-allowed" : "pointer",
              opacity: applyLoading ? 0.7 : 1,
              minWidth: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontWeight: 500,
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleDateFilter(startDate, endDate, dateRangeType)}
            disabled={applyLoading}
          >
            {applyLoading && (
              <div
                style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #ffffff',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite'
                }}
              />
            )}
            <span>{applyLoading ? 'Applying...' : 'Apply'}</span>
          </button>

          <button
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              background: resetLoading ? "#d3d3d3" : "#eee",
              color: "#333",
              border: "1px solid #ccc",
              cursor: resetLoading ? "not-allowed" : "pointer",
              opacity: resetLoading ? 0.6 : 1,
              fontWeight: 500,
              transition: 'all 0.2s ease',
              minWidth: '70px'
            }}
            onClick={handleReset}
            disabled={resetLoading}
          >
            {resetLoading ? 'Resetting...' : 'Reset'}
          </button>
        </div>
      </div>

      <div className="kpi-banner">
        <div className="kpi-left">
          <div className="kpi-card">
            <div className="kpi-title">Avg Achievement</div>
            <div className="kpi-value">{totals.avgPct}%</div>
            <div className="kpi-sub">Across shown negotiators</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-title">Top Negotiator</div>
            <div className="kpi-value">
              {(() => {
                console.log('🎯 Full totals object:', JSON.stringify(totals, null, 2));
                console.log('🎯 totals.top:', totals.top);
                console.log('🎯 totals.top keys:', totals.top ? Object.keys(totals.top) : 'null');
                
                if (!totals.top) {
                  return "— (No data)";
                }
                
                const name = totals.top.name;
                const percentage = totals.top.pct; 
                
                console.log('🎯 Extracted values:', { name, percentage, type: typeof percentage });
                
                if (!name) {
                  return "— (No name)";
                }
                
                if (percentage === undefined || percentage === null) {
                  return `${name} — (No percentage in object)`;
                }
                
                if (isNaN(percentage)) {
                  return `${name} — ${percentage} (NaN)`;
                }
                
                return `${name} — ${percentage}%`;
              })()}
            </div>
            <div className="kpi-sub">Highest % From Goal</div>
          </div>
        </div>

        <div className="kpi-right">
          <button className="export-btn" onClick={exportCSV}>
            <FaFileExport /> Export to CSV
          </button>
        </div>
      </div>

      <div className="filter-search-row">
        <div className="dropdown-container">
          <label htmlFor="negotiator-select">Filter by Negotiator:</label>
          <select
            id="negotiator-select"
            value={selectedNegotiator}
            onChange={(e) => setSelectedNegotiator(e.target.value)}
          >
            <option value="">All</option>
            {[...new Set(data.map((d) => d.AssignedNegotiator))].map((name, index) => (
              <option key={`${name}-${index}`} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {errorMsg && <div className="fetch-error">{errorMsg}</div>}

      <div className="summary-cards">
        <div className="card total-production">
          <h4>Total Production</h4>
          <p className="value">${aggregatedData.reduce((acc, item) => acc + item.totalProductionToDate, 0).toLocaleString()}</p>
          <p className="subtext" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            {isEditingTeamGoal ? (
              <>
                <span>Goal: $</span>
                <input
                  type="number"
                  value={teamGoalInput}
                  onChange={(e) => setTeamGoalInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleTeamGoalSave();
                    if (e.key === 'Escape') handleTeamGoalCancel();
                  }}
                  style={{
                    width: '120px',
                    padding: '4px 8px',
                    border: '2px solid #3498db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  autoFocus
                />
                <button
                  onClick={handleTeamGoalSave}
                  style={{
                    padding: '4px 8px',
                    background: '#2ecc71',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  ✓
                </button>
                <button
                  onClick={handleTeamGoalCancel}
                  style={{
                    padding: '4px 8px',
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  ✕
                </button>
              </>
            ) : (
              <>
                <span>Goal: ${teamGoal.toLocaleString()}</span>
                <button
                  onClick={handleTeamGoalEdit}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3498db',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '0 4px',
                    marginLeft: '4px'
                  }}
                  title="Edit team goal"
                >
                  ✏️
                </button>
              </>
            )}
          </p>
        </div>

        <div className="card working-pipeline">
          <h4>Working Pipeline</h4>
          <p className="value">${aggregatedData.reduce((acc, item) => acc + item.totalProductionUnderway, 0).toLocaleString()}</p>
          <p className="subtext">Active negotiations</p>
        </div>

        <div className="card active-sifs">
          <h4>Active SIFs</h4>
          <p className="value">{aggregatedData.reduce((acc, item) => acc + item.totalSIFsUnderway, 0)}</p>
          <p className="subtext">Settlement files</p>
        </div>

        <div className="card production-potential">
          <h4>Production Potential</h4>
          <p className="value">
            ${(() => {
              const totalProductionToDate = aggregatedData.reduce((acc, item) => acc + (item.totalProductionToDate || 0), 0);
              const totalProductionUnderway = aggregatedData.reduce((acc, item) => acc + (item.totalProductionUnderway || 0), 0);
              const productionPotential = totalProductionToDate + totalProductionUnderway;
              
              console.log('📊 Production Potential Calculation (FIXED):');
              console.log('  Total Production To Date:', totalProductionToDate.toLocaleString());
              console.log('  Total Production Underway:', totalProductionUnderway.toLocaleString());
              console.log('  Production Potential (sum):', productionPotential.toLocaleString());
              console.log('  Aggregated Data Count:', aggregatedData.length);
              
              return productionPotential.toLocaleString();
            })()}
          </p>
          <p className="subtext">Production + Pipeline</p>
        </div>

        <div className="card needed-to-goal">
          <h4>Needed to Goal</h4>
          <p className="value">
            ${(() => {
              const totalProductionToDate = aggregatedData.reduce((acc, item) => acc + (item.totalProductionToDate || 0), 0);
              const totalProductionUnderway = aggregatedData.reduce((acc, item) => acc + (item.totalProductionUnderway || 0), 0);
              const productionPotential = totalProductionToDate + totalProductionUnderway;
              const needed = teamGoal - productionPotential;
              
              console.log('🎯 Needed to Goal Calculation (FIXED):');
              console.log('  Team Goal:', teamGoal.toLocaleString());
              console.log('  Production Potential:', productionPotential.toLocaleString());
              console.log('  Needed to Goal:', needed.toLocaleString());
              
              return needed.toLocaleString();
            })()}
          </p>
          <p className="subtext">Gap to goal</p>
        </div>
      </div>

      <div className="charts-container">
        <div className="chart-box">
          <h3>Goal Achievement by Negotiator</h3>
          <div className="chart-wrapper">
            <Bar
              ref={goalChartRef}
              data={{
                labels: chartLabels,
                datasets: [
                  {
                    label: "Achievement %",
                    data: goalAchievement,
                    backgroundColor: goalAchievement.map((val) => (val >= 60 ? "#f39c12" : "#e74c3c")),
                  },
                ],
              }}
              options={{
                responsive: true,
                animation: { duration: 700 },
                plugins: { legend: { display: false } },
                onClick: (evt, elements) => handleBarClick(evt, elements, chartLabels),
                scales: {
                  y: { beginAtZero: true, max: 150, title: { display: true, text: "Achievement %" } },
                },
              }}
            />
          </div>
        </div>

        <div className="chart-box">
          <h3>Production vs Goal Comparison</h3>
          <div className="chart-wrapper">
            <Bar
              ref={compChartRef}
              data={{
                labels: chartLabels,
                datasets: [
                  { label: "Goal", data: goalsList, backgroundColor: "#dcdcdc" },
                  { label: "Current Production", data: currentProduction, backgroundColor: "#3498db" },
                  { label: "Working Pipeline", data: workingPipeline, backgroundColor: "#9b59b6" },
                ],
              }}
              options={{
                responsive: true,
                animation: { duration: 700 },
                plugins: { legend: { position: "top" } },
                onClick: (evt, elements) => handleBarClick(evt, elements, chartLabels),
                scales: { y: { beginAtZero: true, title: { display: true, text: "Amount ($)" } } },
              }}
            />
          </div>
        </div>
      </div>

      <table className="summary-table">
        <thead>
          <tr>
            <th>Name</th>
            {!hidePastGoalColumns && <th>Goal (Editable)</th>}
            <th>Entries</th>
            <th>Total Production To Date</th>
            <th>Total Production Underway</th>
            <th>Total SIFs Underway</th>
            <th>Total Biggest Fee Pending</th>
            <th>Total Individual Fee</th>
            <th>Total Production Potential</th>
            {!hidePastGoalColumns && (
              <>
                <th>Needed to Goal</th>
                <th>% From Goal</th>
              </>
            )}
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {aggregatedData.map((negotiatorData, index) => (
            <tr key={index}>
              <td>
                <button
                  onClick={() => openDrilldown(negotiatorData.negotiator)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3498db',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: drilldownNegotiator === negotiatorData.negotiator ? '600' : '400',
                    fontSize: '14px'
                  }}
                >
                  {negotiatorData.negotiator}
                </button>
              </td>
              {!hidePastGoalColumns && <td>${negotiatorData.goal.toLocaleString()}</td>}
              <td>
                {negotiatorProductionCounts[negotiatorData.negotiator] !== undefined 
                  ? negotiatorProductionCounts[negotiatorData.negotiator]
                  : <span style={{ color: '#9ca3af' }}>Loading...</span>
                }
              </td>
              <td>${negotiatorData.totalProductionToDate.toLocaleString()}</td>
              <td>${negotiatorData.totalProductionUnderway.toLocaleString()}</td>
              <td>{negotiatorData.totalSIFsUnderway}</td>
              <td>${negotiatorData.biggestFeePending.toLocaleString()}</td>
              <td>${negotiatorData.totalIndividualFee.toLocaleString()}</td>
              <td>${negotiatorData.totalProductionPotential.toLocaleString()}</td>
              {!hidePastGoalColumns && (
                <>
                  <td>${negotiatorData.neededToGoal.toLocaleString()}</td>
                  <td>{negotiatorData.percentFromGoal}%</td>
                </>
              )}
              <td>
                <button
                  onClick={() => openModal(negotiatorData.negotiator)}
                  style={{ 
                    background: "none", 
                    border: "none", 
                    cursor: "pointer", 
                    color: "#3498db", 
                    fontSize: "20px" 
                  }}
                  title="View details"
                >
                  <FaEye />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {drilldownNegotiator && (
        <div className="drilldown-section" ref={drilldownRef}>
          <DrilldownSection
            negotiatorName={drilldownNegotiator}
            onClose={closeDrilldown}
            settlements={drilldownData}
            loading={drilldownLoading}
            startDate={appliedStartDate}
            endDate={appliedEndDate}
          />
        </div>
      )}

      {modalOpen && modalNegotiator && (
        <PP_NegotiatorDetailModal 
          negotiator={modalNegotiator} 
          onClose={closeModal} 
          onSaveSuccess={handleGoalSaved} 
          appliedStartDate={appliedStartDate}
        />
      )}
    </div>
  );
}

function DrilldownSection({ 
  negotiatorName, 
  onClose, 
  settlements = [], 
  loading = false, 
  startDate = null, 
  endDate = null 
}) {
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Dynamically generate statuses from actual settlement data
  const statuses = useMemo(() => {
    const uniqueStatuses = new Set();
    settlements.forEach(s => {
      const status = s.OfferStatus?.trim();
      if (status) {
        uniqueStatuses.add(status);
      }
    });
    return Array.from(uniqueStatuses).sort();
  }, [settlements]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize with all statuses selected when settlements load
  useEffect(() => {
    if (settlements.length > 0 && selectedStatuses.length === 0) {
      setSelectedStatuses([...statuses]);
    }
  }, [settlements, statuses]);

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        // Remove the status
        return prev.filter(s => s !== status);
      } else {
        // Add the status
        return [...prev, status];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedStatuses.length === statuses.length) {
      // Deselect all
      setSelectedStatuses([]);
    } else {
      // Select all
      setSelectedStatuses([...statuses]);
    }
  };

  const filteredSettlements = useMemo(() => {
    if (selectedStatuses.length === 0) return [];
    
    return settlements.filter(s => {
      const status = s.OfferStatus?.trim() || "";
      return selectedStatuses.some(selected => 
        status.toLowerCase() === selected.toLowerCase()
      );
    });
  }, [settlements, selectedStatuses]);

  const totalProductionPotential = useMemo(() => {
    return settlements.reduce((sum, s) => {
      const fee = parseFloat(s.SettlementFee || s.Fee || 0);
      return sum + (isNaN(fee) ? 0 : fee);
    }, 0);
  }, [settlements]);

  // Updated to match the main table's production calculation logic
  const productionStatuses = [
    "missing letter",
    "missing letter (pays submitted)",
    "completed",
    "ready for processing",
    "ready for full processing",
    "ready for partial processing",
  ];
  
  const productionToDate = useMemo(() => {
    return settlements.reduce((sum, s) => {
      const status = (s.OfferStatus || s.Status || "").trim().toLowerCase();
      const fee = parseFloat(s.SettlementFee || s.Fee || 0);
      
      // Check if the status matches any of the production statuses
      const isProductionStatus = productionStatuses.some(prodStatus => 
        status === prodStatus.toLowerCase()
      );
      
      if (isProductionStatus && !isNaN(fee)) {
        return sum + fee;
      }
      return sum;
    }, 0);
  }, [settlements]);

  const totalSettlementFee = useMemo(() => {
    return filteredSettlements.reduce((sum, s) => {
      const fee = parseFloat(s.SettlementFee || s.Fee || 0);
      return sum + (isNaN(fee) ? 0 : fee);
    }, 0);
  }, [filteredSettlements]);

  const exportDrilldownCSV = () => {
    // Filter settlements by production statuses before exporting
    const productionStatuses = [
      "missing letter",
      "missing letter (pays submitted)",
      "completed",
      "ready for processing",
      "ready for full processing",
      "ready for partial processing",
    ];

    const productionSettlements = filteredSettlements.filter(s => {
      const status = (s.OfferStatus || s.Status || "").trim().toLowerCase();
      return productionStatuses.some(prodStatus => 
        status === prodStatus.toLowerCase()
      );
    });

    const header = [
      "Contact ID",
      "Debt ID", 
      "Settlement ID", 
      "Creditor", 
      "Created Date", 
      "Offer Status", 
      "Settlement Fee", 
      "Negotiator"
    ];

    const rowsCsv = [header.join(",")];

    productionSettlements.forEach((s) => {
      const row = [
        `"${s.ContactId || ''}"`,
        `"${s.DebtId || ''}"`,
        `"${s.SettlementId || ''}"`,
        `"${s.Creditor || ''}"`,
        `"${s.CreatedDate 
          ? new Date(s.CreatedDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }) 
          : ''}"`,
        `"${s.OfferStatus || ''}"`,
        `"${s.SettlementFee || s.Fee 
          ? `${parseFloat(s.SettlementFee || s.Fee).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}` 
          : ''}"`,
        `"${s.AssignedNegotiator || s.Negotiator || ''}"`,
      ].join(",");

      rowsCsv.push(row);
    });

    const blob = new Blob([rowsCsv.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const formatDateForFilename = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const startDateFormatted = formatDateForFilename(startDate);
    const endDateFormatted = formatDateForFilename(endDate);
    const fileName = `${negotiatorName}_Settlements_${startDateFormatted || 'Current'}_to_${endDateFormatted || 'Current'}_ProductionOnly.csv`;
    
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDateRange = () => {
    if (!startDate && !endDate) {
      return "Current Month";
    }
    
    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    };

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    if (start && end) {
      return `${start} - ${end}`;
    } else if (start) {
      return `From ${start}`;
    } else if (end) {
      return `Until ${end}`;
    }
    
    return "Current Month";
  };

  return (
    <>
      <div className="drilldown-header">
        <div>
          <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>
            {negotiatorName} — Settlement Details
          </h3>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            color: '#666',
            fontSize: '14px'
          }}>
            <span style={{ 
              background: '#e3f2fd', 
              padding: '4px 8px', 
              borderRadius: '4px',
              color: '#1976d2',
              fontWeight: '500'
            }}>
              📅 {formatDateRange()}
            </span>

            {!loading && (
              <>
                <span>•</span>
                <span>
                  {filteredSettlements.length} settlement{filteredSettlements.length !== 1 ? 's' : ''} displayed
                </span>
                <span>•</span>
                <span style={{ color: '#2563eb', fontWeight: '500' }}>
                  {settlements.filter(s => {
                    const status = (s.OfferStatus || s.Status || "").trim().toLowerCase();
                    const productionStatuses = [
                      "missing letter",
                      "missing letter (pays submitted)",
                      "completed",
                      "ready for processing",
                      "ready for full processing",
                      "ready for partial processing",
                    ];
                    return productionStatuses.some(prodStatus => status === prodStatus.toLowerCase());
                  }).length} production settlements
                </span>
                <span>•</span>
                <span style={{ color: "#16a34a", fontWeight: "bold" }}>
                  ${totalSettlementFee.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {!loading && settlements.length > 0 && (
            <button 
              onClick={exportDrilldownCSV}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#2ecc71',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background 0.2s',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.background = '#27ae60'}
              onMouseLeave={(e) => e.target.style.background = '#2ecc71'}
            >
              <FaFileExport /> Export CSV
            </button>
          )}

          <button 
            onClick={onClose} 
            style={{
              padding: '8px 16px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'background 0.2s',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.background = '#dc2626'}
            onMouseLeave={(e) => e.target.style.background = '#ef4444'}
          >
            <FaTimes /> Close
          </button>
        </div>
      </div>

      {!loading && settlements.length > 0 && (
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            background: 'white',
            padding: '16px 20px',
            borderRadius: '8px',
            border: '2px solid #e0e0e0',
            flex: '1',
            minWidth: '250px'
          }}>
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              fontWeight: '600', 
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Total Production Potential
            </div>
            <div style={{ 
              fontSize: '24px', 
              color: "#2563eb", 
              fontWeight: "700"
            }}>
              ${totalProductionPotential.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          
          <div style={{
            background: 'white',
            padding: '16px 20px',
            borderRadius: '8px',
            border: '2px solid #e0e0e0',
            flex: '1',
            minWidth: '250px'
          }}>
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              fontWeight: '600', 
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Production to Date
            </div>
            <div style={{ 
              fontSize: '24px', 
              color: "#10b981", 
              fontWeight: "700"
            }}>
              ${productionToDate.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>
      )}

      {!loading && settlements.length > 0 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px', 
          marginBottom: '15px',
          background: 'white',
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid #e0e0e0',
          position: 'relative',
          zIndex: 10
        }}>
          <label style={{ fontWeight: '500', whiteSpace: 'nowrap' }}>Filter by Status:</label>
          
          <div ref={dropdownRef} style={{ position: 'relative', minWidth: '300px', flex: 1, maxWidth: '500px' }}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                background: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                fontSize: '14px',
                color: '#1f2937'
              }}
            >
              <span>
                {selectedStatuses.length === statuses.length 
                  ? 'All Statuses' 
                  : `${selectedStatuses.length} selected`}
              </span>
              <span style={{ marginLeft: '8px' }}>▼</span>
            </button>

            {showDropdown && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                background: 'white',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                zIndex: 9999,
                maxHeight: '400px',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                <div style={{
                  padding: '12px 15px',
                  borderBottom: '2px solid #e5e7eb',
                  background: '#f9fafb',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    color: '#1f2937',
                    userSelect: 'none'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSelectAll();
                  }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.length === statuses.length}
                      indeterminate={selectedStatuses.length > 0 && selectedStatuses.length < statuses.length}
                      onChange={() => {}} // Controlled by label click
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      style={{ 
                        marginRight: '12px', 
                        cursor: 'pointer',
                        width: '18px',
                        height: '18px',
                        flexShrink: 0,
                        pointerEvents: 'none'
                      }}
                    />
                    <span>Select All ({statuses.length})</span>
                  </label>
                </div>

                <div style={{ padding: '4px 0' }}>
                  {statuses.map((status) => {
                    const count = settlements.filter(s => 
                      s.OfferStatus?.trim().toLowerCase() === status.toLowerCase()
                    ).length;
                    const isChecked = selectedStatuses.includes(status);
                    
                    return (
                      <div
                        key={status}
                        style={{
                          padding: '10px 15px',
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background 0.15s',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontSize: '14px',
                          width: '100%',
                          color: '#1f2937',
                          userSelect: 'none',
                          gap: '12px'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleStatusToggle(status);
                        }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}} // Controlled by label click
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            style={{ 
                              cursor: 'pointer',
                              width: '18px',
                              height: '18px',
                              flexShrink: 0,
                              pointerEvents: 'none'
                            }}
                          />
                          <span style={{ 
                            flex: 1, 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                            lineHeight: '1.4'
                          }}>
                            {status}
                          </span>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            flexShrink: 0,
                            background: isChecked ? '#dbeafe' : '#f3f4f6',
                            color: isChecked ? '#1e40af' : '#6b7280',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            minWidth: '28px',
                            textAlign: 'center'
                          }}>
                            {count}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <span style={{ color: '#6b7280', fontSize: '14px', whiteSpace: 'nowrap' }}>
            Showing {filteredSettlements.length} of {settlements.length}
          </span>
        </div>
      )}

      <div className="drilldown-table-container">
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px',
            flexDirection: 'column'
          }}>
            <div 
              style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                animation: 'spin 1s linear infinite'
              }}
            />
            <p style={{ marginTop: '15px', color: '#6b7280' }}>
              Loading settlements for {negotiatorName}...
            </p>
          </div>
        ) : (
          <table className="drilldown-table">
            <thead>
              <tr>
                <th>Contact ID</th>
                <th>Debt ID</th>
                <th>Settlement ID</th>
                <th>Creditor</th>
                <th>Created Date</th>
                <th>Offer Status</th>
                <th>Settlement Fee</th>
                <th>Negotiator</th>
              </tr>
            </thead>
            <tbody>
              {filteredSettlements.length === 0 ? (
                <tr>
                  <td 
                    colSpan="8" 
                    style={{ 
                      textAlign: 'center', 
                      padding: '40px', 
                      color: '#6b7280' 
                    }}
                  >
                    {settlements.length === 0 
                      ? `No settlements found for ${negotiatorName} in the selected date range.`
                      : `No settlements found with status "${statusFilter}".`}
                  </td>
                </tr>
              ) : (
                filteredSettlements.map((s, i) => (
                  <tr key={i}>
                    <td>{s.ContactId || "—"}</td>
                    <td>{s.DebtId || "—"}</td>
                    <td>{s.SettlementId || "—"}</td>
                    <td>{s.Creditor || "—"}</td>
                    <td>
                      {s.CreatedDate
                        ? new Date(s.CreatedDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : "—"}
                    </td>
                    <td>
                      <span className={`status-badge ${
                        s.OfferStatus === 'Completed' ? 'status-completed' :
                        s.OfferStatus === 'Working' ? 'status-working' :
                        'status-default'
                      }`}>
                        {s.OfferStatus || "—"}
                      </span>
                    </td>
                    <td>
                      {s.SettlementFee || s.Fee
                        ? `${parseFloat(
                            s.SettlementFee || s.Fee
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : "—"}
                    </td>
                    <td>{s.AssignedNegotiator || s.Negotiator || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export default PP_partial_Summary;